import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type KieuNut = 'chinh' | 'phu' | 'nhe' | 'nguy-hiem';

const LOP: Record<KieuNut, string> = {
  chinh: 'bg-nhan text-white hover:bg-nhan-dam',
  phu: 'bg-be-mat text-chu border border-vien hover:bg-neutral-nen',
  nhe: 'bg-transparent text-chu-phu hover:bg-neutral-nen',
  'nguy-hiem': 'bg-bad-chu text-white hover:opacity-90',
};

/**
 * Nút bị **vô hiệu** luôn xám trung tính, không phải màu của chính nó mờ đi.
 *
 * Trước đây chỉ có `disabled:opacity-60`, nên nút "Xoá tài khoản" lúc không đủ
 * điều kiện vẫn là đỏ nhạt — trông như đang bấm được. Nút đang **chạy**
 * (`dangChay`) thì giữ màu gốc mờ đi, vì nó vừa được bấm chứ không phải bị chặn.
 */
const LOP_VO_HIEU =
  'disabled:border-vien disabled:bg-neutral-nen disabled:text-chu-phu disabled:shadow-none';

/**
 * Cỡ nút. `nho` cao 34px, dùng cho nút **trong một hàng bảng**: hàng cao 38–40px
 * mà nút trong nó cũng 40px thì hàng phình ra gấp đôi (xem `docs/ui.md` mục 1).
 */
export type CoNut = 'thuong' | 'nho';

const LOP_CO: Record<CoNut, string> = {
  thuong: 'vung-bam px-4 text-co-ban rounded-[10px]',
  nho: 'vung-bam-nho px-3 text-co-ban rounded-o',
};

export interface NutProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kieu?: KieuNut;
  co?: CoNut;
  dangChay?: boolean;
  children?: ReactNode;
}

export function Nut({
  kieu = 'phu',
  co = 'thuong',
  dangChay = false,
  className = '',
  children,
  disabled,
  ...rest
}: NutProps) {
  // Bị chặn (xám) khác với đang chạy (màu gốc, mờ đi).
  const biChan = (disabled ?? false) && !dangChay;

  return (
    <button
      type="button"
      {...rest}
      disabled={disabled ?? dangChay}
      className={`inline-flex items-center justify-center gap-2 font-semibold transition-colors disabled:cursor-not-allowed ${
        LOP_CO[co]
      } ${biChan ? LOP_VO_HIEU : 'disabled:opacity-60'} ${LOP[kieu]} ${className}`}
    >
      {dangChay && (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
}
